import { useState, useEffect } from 'react';
import {
  X,
  LayoutDashboard,
  Camera,
  UserCircle,
  Building2,
  Download,
  ChevronRight,
  ChevronLeft,
  Sparkles,
} from 'lucide-react';

const STORAGE_KEY = 'dominate-insights-tutorial-dismissed';

interface Step {
  icon: typeof LayoutDashboard;
  title: string;
  description: string;
  details: string[];
}

const steps: Step[] = [
  {
    icon: Sparkles,
    title: 'Welcome to HumanVu',
    description: 'Your AI-powered visitor analytics platform. Understand foot traffic patterns, engagement levels, and peak hours across your locations.',
    details: [
      'Real-time anonymous visitor counting and trend analysis',
      'Attention tracking and engagement scoring',
      'Multi-branch support for all your locations',
      'No personal information is collected or stored',
    ],
  },
  {
    icon: Camera,
    title: 'Set Up Cameras',
    description: 'Register cameras from the Cameras page, then install the desktop client on each machine with a webcam.',
    details: [
      'Click "Add Camera" to generate an API key',
      'Download the client app from the Downloads page',
      'Enter the Camera ID and API key in the client',
    ],
  },
  {
    icon: LayoutDashboard,
    title: 'Analytics Dashboard',
    description: 'Your main hub for visitor insights. Filter by time period and branch to see trends at a glance.',
    details: [
      'Total visitors, unique viewers, and peak hours',
      'Aggregated demographic insights (estimated age groups, sentiment)',
      'Engagement tiers: Glancers, Browsers, and Engaged',
    ],
  },
  {
    icon: UserCircle,
    title: 'Face Recognition',
    description: 'Recognise returning visitors to measure repeat foot traffic. No photos or personally identifiable information is ever stored.',
    details: [
      'Only anonymous numerical embeddings are kept — images are discarded immediately',
      'Returning visitors are counted without identifying who they are',
      'Assign optional labels for your own reference (e.g. "Staff", "Regular")',
      'Fully POPIA-compliant — no personal data is collected',
    ],
  },
  {
    icon: Building2,
    title: 'Branches & Multi-Location',
    description: 'Organize cameras across multiple physical locations and compare performance.',
    details: [
      'Create branches for each store or site',
      'Assign cameras to branches for grouped analytics',
      'Filter the dashboard by branch to compare locations',
    ],
  },
  {
    icon: Download,
    title: 'Desktop Client',
    description: 'The HumanVu client runs on Windows and Linux. It auto-updates when new versions are released.',
    details: [
      'Runs face detection locally — no video leaves the device',
      'Only anonymous analytics data is sent to the server',
      'Auto-updates silently in the background',
    ],
  },
];

export function useShowTutorial() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (!dismissed) {
      setShow(true);
    }
  }, []);

  const open = () => setShow(true);
  const close = () => {
    setShow(false);
    localStorage.setItem(STORAGE_KEY, 'true');
  };
  const reset = () => {
    localStorage.removeItem(STORAGE_KEY);
    setShow(true);
  };

  return { show, open, close, reset };
}

interface GettingStartedProps {
  show: boolean;
  onClose: () => void;
}

export default function GettingStarted({ show, onClose }: GettingStartedProps) {
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    if (show) setCurrentStep(0);
  }, [show]);

  if (!show) return null;

  const step = steps[currentStep];
  const isFirst = currentStep === 0;
  const isLast = currentStep === steps.length - 1;
  const Icon = step.icon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-lg mx-4 bg-white dark:bg-slate-800 rounded-2xl shadow-2xl dark:shadow-black/40 overflow-hidden">
        {/* Header gradient */}
        <div className="bg-gradient-to-br from-primary-600 to-primary-700 px-6 pt-6 pb-8">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1.5 rounded-lg text-white/70 hover:text-white hover:bg-white/10 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-3 mb-3">
            <div className="p-2.5 bg-white/15 rounded-xl">
              <Icon className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-xs text-white/60 font-medium uppercase tracking-wider">
                Step {currentStep + 1} of {steps.length}
              </p>
              <h2 className="text-xl font-bold text-white">{step.title}</h2>
            </div>
          </div>

          <p className="text-sm text-white/80 leading-relaxed">{step.description}</p>
        </div>

        {/* Content */}
        <div className="px-6 py-5">
          <ul className="space-y-3">
            {step.details.map((detail, i) => (
              <li key={i} className="flex items-start gap-3">
                <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary-500 flex-shrink-0" />
                <span className="text-sm text-gray-600 dark:text-slate-300 leading-relaxed">{detail}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 dark:border-slate-700">
          {/* Step dots */}
          <div className="flex gap-1.5">
            {steps.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentStep(i)}
                className={`w-2 h-2 rounded-full transition-all ${
                  i === currentStep
                    ? 'bg-primary-500 w-6'
                    : i < currentStep
                    ? 'bg-primary-300 dark:bg-primary-700'
                    : 'bg-gray-200 dark:bg-slate-600'
                }`}
              />
            ))}
          </div>

          {/* Navigation */}
          <div className="flex gap-2">
            {!isFirst && (
              <button
                onClick={() => setCurrentStep(currentStep - 1)}
                className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                Back
              </button>
            )}
            {isLast ? (
              <button
                onClick={onClose}
                className="px-4 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
              >
                Get Started
              </button>
            ) : (
              <button
                onClick={() => setCurrentStep(currentStep + 1)}
                className="flex items-center gap-1 px-4 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
